import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSaleOutlined";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCartOutlined";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonthOutlined";
import WarehouseIcon from "@mui/icons-material/WarehouseOutlined";
import BadgeIcon from "@mui/icons-material/BadgeOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2Outlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import agLogo from "./sales/ag-logo.png";

export default function Navbar({ onLogout, onToggle }) {
  const location = useLocation();
  const sidebar = useRef(null);
  const mobileToggle = useRef(null);
  let currentUser = {};
  try { currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}"); } catch {}
  const role = String(currentUser?.role || "").toLowerCase();
  const raw = currentUser?.access || [];
  let accessList = [];
  if (Array.isArray(raw)) accessList = raw;
  else if (raw && typeof raw === "object") accessList = Object.keys(raw).filter((key) => raw[key]);
  else if (typeof raw === "string") {
    try { accessList = JSON.parse(raw); } catch { accessList = raw.split(","); }
  }
  accessList = (Array.isArray(accessList) ? accessList : []).map((key) => String(key).toLowerCase().replace(/\s+/g, ""));
  const hasAccess = (key) => role === "admin" || accessList.includes(key.toLowerCase());
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "true");
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 900px)").matches);
  const expanded = mobile || !collapsed;
  const navItems = [];
  if (["limited", "admin", "purchaser"].includes(role)) {
    navItems.push({ path: "/dashboard", label: "Overview", icon: <DashboardIcon /> });
    if (hasAccess("salesbyitems") || hasAccess("salesitems1")) navItems.push({ label: "Sales", icon: <PointOfSaleIcon />, subItems: [
      ...(hasAccess("salesbyitems") ? [{ path: "/salesbyitems", label: "By Customer" }] : []),
      ...(hasAccess("salesitems1") ? [{ path: "/SalesItems1", label: "By Items" }] : []),
    ] });
    if (hasAccess("purchaseitems") || hasAccess("purchasebycustomer")) navItems.push({ label: "Purchases", icon: <ShoppingCartIcon />, subItems: [
      ...(hasAccess("purchaseitems") ? [{ path: "/PurchaseItems", label: "By Items" }] : []),
      ...(hasAccess("purchasebycustomer") ? [{ path: "/purchasebycustomer", label: "By Supplier" }] : []),
    ] });
    for (const [key, route, label, Icon] of [
      ["purchaserreport", "/PurchaserReport", "Purchaser Report", GroupsIcon],
      ["monthlybudget1", "/MonthlyBudget1", "Monthly Budget", CalendarMonthIcon],
      ["stockentry", "/StockForm", "Stock Entry", WarehouseIcon],
      ["employeereport", "/EmpGapForm", "Employee Report", BadgeIcon],
      ["itemtable", "/itemtable", "Items Entry", Inventory2Icon],
    ]) if (hasAccess(key)) navItems.push({ path: route, label, icon: <Icon /> });
    if (role === "admin") navItems.push({ path: "/useraccesscontrol", label: "Roles & Access", icon: <AdminPanelSettingsIcon /> });
  }
  const isActive = (route) => location.pathname.toLowerCase() === route.toLowerCase();
  const activeParent = navItems.find((item) => item.subItems?.some((sub) => isActive(sub.path)));
  const activeLabel = activeParent?.subItems.find((sub) => isActive(sub.path))?.label || navItems.find((item) => item.path && isActive(item.path))?.label || "Workspace";

  useEffect(() => { onToggle?.(collapsed); }, [collapsed, onToggle]);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 900px)");
    const update = () => { setMobile(query.matches); if (!query.matches) setMobileOpen(false); };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    setOpenMenu(activeParent?.label || null);
    setMobileOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!mobile || !mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    const portalRoot = document.getElementById("root");
    const previousRootOverflow = portalRoot?.style.overflow;
    document.body.style.overflow = "hidden";
    if (portalRoot) portalRoot.style.overflow = "hidden";
    sidebar.current?.querySelector("button, a")?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") { event.preventDefault(); setMobileOpen(false); }
      if (event.key === "Tab") {
        const candidates = [...sidebar.current.querySelectorAll("button, a[href]")].filter((element) => element.getClientRects().length);
        const first = candidates[0], last = candidates[candidates.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", keydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      if (portalRoot) portalRoot.style.overflow = previousRootOverflow || "";
      document.removeEventListener("keydown", keydown);
      mobileToggle.current?.focus();
    };
  }, [mobile, mobileOpen]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar-collapsed", String(next));
  }
  const username = currentUser?.username || "User";
  return <>
    <header className="portal-topbar">
      <button ref={mobileToggle} className="portal-icon-button portal-mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="portal-navigation"><MenuIcon /></button>
      <div className="portal-breadcrumb"><span>Workspace</span><ChevronRightIcon /><strong>{activeParent ? `${activeParent.label} / ${activeLabel}` : activeLabel}</strong></div>
      <div className="portal-topbar-account"><span className="portal-avatar">{username.slice(0, 1).toUpperCase()}</span><span><strong>{username}</strong><small>{currentUser?.role || "Member"}</small></span></div>
    </header>
    {mobileOpen && mobile && <button className="portal-nav-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" tabIndex={-1} />}
    <aside ref={sidebar} id="portal-navigation" className={`portal-sidebar ${expanded ? "is-expanded" : "is-compact"} ${mobileOpen ? "is-open" : ""}`} aria-label="Main navigation" role={mobileOpen && mobile ? "dialog" : undefined} aria-modal={mobileOpen && mobile ? true : undefined} inert={mobile && !mobileOpen ? "" : undefined}>
      <div className="portal-brand-row"><Link to="/dashboard" className="portal-brand" aria-label="Ali Gholami Reports home"><img src={agLogo} alt="Ali Gholami" />{expanded && <span><strong>AG Reports</strong><small>Business workspace</small></span>}</Link>
        {mobile ? <button className="portal-icon-button" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><CloseIcon /></button> : expanded && <button className="portal-collapse-button" onClick={toggleCollapsed} aria-label="Collapse sidebar"><ChevronLeftIcon /></button>}
      </div>
      {!expanded && <button className="portal-expand-button" onClick={toggleCollapsed} aria-label="Expand sidebar"><ChevronRightIcon /></button>}
      <div className="portal-nav-caption">{expanded ? "WORKSPACE" : "—"}</div>
      <nav className="portal-navigation">
        {navItems.map((item) => {
          const active = item.path ? isActive(item.path) : item.subItems.some((sub) => isActive(sub.path));
          const open = openMenu === item.label;
          return <div className="portal-nav-group" key={item.label}>
            {item.path ? <Link title={!expanded ? item.label : undefined} className={`portal-nav-link ${active ? "is-active" : ""}`} to={item.path} aria-current={active ? "page" : undefined}><span className="portal-nav-icon">{item.icon}</span>{expanded && <span>{item.label}</span>}</Link> : <>
              <button className={`portal-nav-link ${active ? "is-active" : ""}`} title={!expanded ? item.label : undefined} aria-label={item.label} aria-expanded={expanded && open} onClick={() => { if (!expanded) { setCollapsed(false); localStorage.setItem("sidebar-collapsed", "false"); } setOpenMenu(open && expanded ? null : item.label); }}><span className="portal-nav-icon">{item.icon}</span>{expanded && <><span>{item.label}</span><ExpandMoreIcon className={`portal-nav-chevron ${open ? "is-open" : ""}`} /></>}</button>
              {expanded && open && <div className="portal-subnav">{item.subItems.map((sub) => <Link key={sub.path} to={sub.path} className={isActive(sub.path) ? "is-active" : ""} aria-current={isActive(sub.path) ? "page" : undefined}><span className="portal-subnav-dot" />{sub.label}</Link>)}</div>}
            </>}
          </div>;
        })}
      </nav>
      <div className="portal-sidebar-footer">{expanded && <div className="portal-workspace-note"><span className="portal-status-dot" /><span>Ali Gholami Trading<small>Reports & operations</small></span></div>}<button className="portal-nav-link portal-signout" title="Sign out" onClick={onLogout}><span className="portal-nav-icon"><LogoutIcon /></span>{expanded && "Sign out"}</button></div>
    </aside>
  </>;
}
